import { useRef, useEffect, useCallback } from "react";
import { validateTargetingConditions as validateConditions } from "../utils/targetingConditionsValidation";
import { useTcStayLeadConditions } from "./useTcStayLeadConditions";
import { useTcDateRanges } from "./useTcDateRanges";

/**
 * Custom hook for managing TargetingConditions component state
 * Extracts all state management, effects, and handlers from TargetingConditions.js
 *
 * Composes two sub-hooks: `useTcDateRanges` (booking/stay/blackout date
 * state + handlers) and `useTcStayLeadConditions` (lead-time + length-of-
 * stay state + handlers). The parent owns `isInternalUpdate`, the shared
 * ref both sub-hooks read in their promo-sync effects to skip clobbering
 * locally-edited fields. The parent's own promo-sync effect runs last
 * (effects fire in declaration order) and resets the flag once both
 * sub-hooks have observed it.
 *
 * Stay dates are multi-range (EO-1492): `stayDateRanges` plus its handlers
 * come straight from `useTcDateRanges`.
 */
export function useTargetingConditionsState({
  promo,
  handleFieldChange,
  tc,
  setTc,
  setIsNextBtnDisabled,
  // Editing an existing promo: locked past start dates are not errors the
  // user can fix, so past-date validation is relaxed (HTT-9780).
  isEditingExistingPromo = false,
}) {
  const isInternalUpdate = useRef(false);

  const handleInternalFieldChange = useCallback(
    (path, value) => {
      isInternalUpdate.current = true;
      handleFieldChange(path, value);
    },
    [handleFieldChange]
  );

  const dateRangesState = useTcDateRanges({
    promo,
    tc,
    setTc,
    handleFieldChange,
    handleInternalFieldChange,
    setIsNextBtnDisabled,
    isInternalUpdateRef: isInternalUpdate,
    isEditingExistingPromo,
  });
  const {
    fromBooking,
    toBooking,
    setShowBooking,
    handleBookingChange,
    stayDateRanges,
    setShowPSD,
    handleStayDateRangeChange,
    addStayDateRange,
    removeStayDateRange,
    stayDateRangeErrors,
    blackoutDates,
    setShowBlackouts,
    setBODates,
    addBlackoutDate,
    removeBlackoutDate,
    handleAllowStayThrough,
    hasStayDateError,
    hasBookingDateError,
  } = dateRangesState;

  const ltLosState = useTcStayLeadConditions({
    promo,
    tc,
    setTc,
    handleFieldChange,
    handleInternalFieldChange,
    isInternalUpdateRef: isInternalUpdate,
  });
  const {
    ltType,
    setLTType,
    minBookingLeadTime,
    maxBookingLeadTime,
    setShowLT,
    setLTMinMax,
    setLTRange,
    losType,
    setLOSType,
    minLos,
    maxLos,
    setShowLOS,
    setLosMinMax,
    setLosRange,
    hasLeadTimeError,
    hasLosError,
  } = ltLosState;

  // Reset the internal-update flag after both sub-hooks have observed it.
  // Effects fire in declaration order, so this runs AFTER both sub-hooks'
  // promo-sync effects above. The flag stays set through the sub-hook
  // reads, and we clear it here so the next external promo update is
  // treated as such.
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
    }
  }, [promo]);

  // Validation function wrapper using the extracted utility
  const validateTargetingConditions = useCallback(() => {
    return validateConditions({
      minLos: tc.showLOS ? minLos : null,
      maxLos: tc.showLOS ? maxLos : null,
      minLeadTime: tc.showLT ? minBookingLeadTime : null,
      maxLeadTime: tc.showLT ? maxBookingLeadTime : null,
      showBooking: tc.showBooking,
      fromBooking,
      toBooking,
      showPSD: tc.showPSD,
      // EO-1492: multi-range stay dates. Empty when the section is hidden so a
      // stale selection can't fail validation for a promo that no longer has it.
      stayDateRanges: tc.showPSD ? stayDateRanges : [],
      // EO-1492 Feature 3: blackouts must fall within a stay date range.
      blackoutDates: tc.showBlackouts ? blackoutDates : [],
      isEditingExistingPromo,
    });
  }, [
    minLos,
    maxLos,
    minBookingLeadTime,
    maxBookingLeadTime,
    tc.showLOS,
    tc.showLT,
    tc.showBooking,
    fromBooking,
    toBooking,
    tc.showPSD,
    stayDateRanges,
    tc.showBlackouts,
    blackoutDates,
    isEditingExistingPromo,
  ]);

  return {
    // Booking window
    fromBooking,
    toBooking,
    setShowBooking,
    handleBookingChange,

    // Stay dates (EO-1492 multi-range)
    stayDateRanges,
    setShowPSD,
    handleStayDateRangeChange,
    addStayDateRange,
    removeStayDateRange,
    stayDateRangeErrors,

    // Blackout dates
    blackoutDates,
    setShowBlackouts,
    setBODates,
    addBlackoutDate,
    removeBlackoutDate,
    handleAllowStayThrough,

    // Lead time
    ltType,
    setLTType,
    minBookingLeadTime,
    maxBookingLeadTime,
    setShowLT,
    setLTMinMax,
    setLTRange,

    // Length of stay
    losType,
    setLOSType,
    minLos,
    maxLos,
    setShowLOS,
    setLosMinMax,
    setLosRange,

    // Validation
    validateTargetingConditions,
    hasLosError,
    hasLeadTimeError,
    hasStayDateError,
    hasBookingDateError,
  };
}
